import actionInfo from 'cio/utils/action-info';
import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import { PlatformNames } from 'cio/utils/analytics/transforms/push';
import { generateSource } from 'cio/utils/analytics/sources';
import NewsletterModel from 'cio/models/newsletter';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { ComposerActionTypeName } from 'cio/utils/composer';
import ActionModel from 'cio/models/action';
import getComposerMessageTypeName from 'cio/utils/analytics/composer-message-type';
import { ComposerEditableEntityAnalyticsData } from 'cio/utils/composer/composer-editable-entity';
import getBuilderType from 'cio/utils/analytics/builder-type';

interface RetryData {
  count: number;
  statuses: ('failed' | 'undeliverable')[];
}

interface AddDataStartedData {
  campaign: CampaignModel | NewsletterModel | TransactionalMessageModel;
  actionTypeName: ComposerActionTypeName;
  liquid: string;
  selectedTab: string;
}

interface AddDataCompletedData extends AddDataStartedData {
  isFallbackSet: boolean;
  fallbackValue: string;
}

interface MinimapViewedData {
  hoverTimeInSeconds: number;
  action: ActionModel;
}

interface MinimapClickedData {
  currentAction: ActionModel;
  clickedAction: ActionModel;
}

export type VisualEditorPanelName = 'code' | 'insert' | 'properties' | 'layers';
export type VisualEditorPropertiesPanelToggleMethod = 'bubble menu' | 'editor tool button';

function actionType(action: any) {
  let info = actionInfo(action.type);
  return info ? info.analyticsName : 'Unknown';
}

function generateRetryData(data: Data & RetryData) {
  return {
    'Selected Message Count': data.count,
    'Selected Statuses': data.statuses,
  };
}

/**
 * A type guard to identify interface: AddDataCompletedData
 */
function isAddDataCompletedData(
  data: (Data & AddDataStartedData) | (Data & AddDataCompletedData),
): data is Data & AddDataCompletedData {
  return (data as Data & AddDataCompletedData).isFallbackSet !== undefined;
}

function generateAddData(data: (Data & AddDataStartedData) | (Data & AddDataCompletedData)) {
  let messageUsedIn!: string;
  let dataType!: string;
  let messageType!: string;

  if (data.campaign instanceof NewsletterModel) {
    messageUsedIn = 'Newsletter';
  } else if (data.campaign instanceof CampaignModel && data.campaign.type === CampaignType.APITriggered) {
    messageUsedIn = 'Triggered Broadcast';
  } else {
    messageUsedIn = 'Campaign';
  }

  // capture messaging types only among composer action types
  messageType = getComposerMessageTypeName(data.actionTypeName);

  switch (data.selectedTab) {
    case 'attributes':
      dataType = 'Profile Attribute';
      break;
    case 'eventData':
      dataType = 'Event Attribute';
      break;
    case 'sampleData':
      dataType = 'Trigger Attribute';
      break;
    case 'objectRelationships':
      // case for objects.<object_type_slug>.relationship.foo
      if (data.liquid.includes('relationship')) {
        dataType = 'Relationship Attribute';
        break;
      }

      dataType = 'Object Attribute';
      break;
    case 'trigger':
      // case for trigger.relationship.foo
      if (data.liquid.includes('relationship')) {
        dataType = 'Relationship Attribute';
        break;
      }
      // case for trigger.customer.foo
      if (data.liquid.includes('customer')) {
        dataType = 'Profile Attribute';
        break;
      }

      // case for trigger.<object_type_slug>.foo
      dataType = 'Object Attribute';
      break;
  }

  let attrs: Record<string, string | boolean> = {
    'Message Used In': messageUsedIn,
    'Message Type': messageType,
    'Data Type': dataType,
    'Data Value': data.liquid,
  };

  if (isAddDataCompletedData(data)) {
    attrs = {
      ...attrs,
      'Fallback Set': data.isFallbackSet,
      'Fallback Value': data.fallbackValue,
    };
  }
  return attrs;
}

function generateCampaignActionData(action: any) {
  let attrs: any = {
    'Message Used In': action.campaign.isApiTriggered ? 'Triggered Broadcast' : 'Campaign',
    'Message Type': actionType(action),
    'Campaign Action ID': action.id,
    'Campaign ID': action.campaign.id,
  };

  return attrs;
}

function generateNewsletterTemplateData(template: any) {
  let attrs: { [key: string]: any } = {};
  let newsletterId = template.belongsTo('newsletter').id();

  if (newsletterId) {
    attrs = {
      Language: template.language,
      'Message Used In': 'Newsletter',
      'Newsletter Content ID': template.id,
      'Newsletter ID': newsletterId,
    };
  } else if (template.action?.campaign) {
    attrs = generateCampaignActionData(template.action);

    if (template.action.isPush) {
      attrs.Platform = (PlatformNames as any)[template.action.sendToPlatform];
    }
  }

  return attrs;
}

function generateBaseData(data: ComposerEditableEntityAnalyticsData) {
  let attrs: { [key: string]: any } = {};

  if (data.isNewsletter) {
    attrs = {
      'Message Used In': 'Newsletter',
      'Newsletter Content ID': data.template?.id,
      'Newsletter ID': data.model.id,
    };
  } else if (data.isCampaign) {
    let campaign = data.model as CampaignModel;
    attrs = {
      'Message Used In': campaign.isApiTriggered ? 'Triggered Broadcast' : 'Campaign',
      'Message Type': actionType(data.action),
      'Campaign Action ID': data.action?.id,
      'Campaign ID': campaign.id,
    };

    if (data.action?.type === 'push_action') {
      attrs.Platform = (PlatformNames as any)[data.action.sendToPlatform];
    }
  } else if (data.isTransactionalMessage) {
    attrs = {
      'Message Used In': 'Transactional Message',
      'Transactional ID': data.model.id,
      'Transactional Content ID': data.template?.id,
    };
  }

  return attrs;
}

function generateMessageMultiLanguageData(data: ComposerEditableEntityAnalyticsData) {
  return {
    ...generateBaseData(data),
    Language: data.language,
    'Language Count': data.languageCount,
  };
}

function generateErrorsData(data: Data & ComposerEditableEntityAnalyticsData) {
  let baseData = generateMessageMultiLanguageData(data);

  return {
    ...baseData,
    'Builder Type': getBuilderType(data),
    'Error Content': Object.keys(data.errors).reduce((acc: any, key: any) => {
      if (!data.errors[key]) {
        return acc;
      }

      return [...acc, ...data.errors[key]];
    }, []),
  };
}

const Transforms: TransformsList = {
  template(data) {
    return generateNewsletterTemplateData(data.model);
  },

  action(data) {
    return generateCampaignActionData(data.model);
  },

  preview(data: Data & { newsletter?: NewsletterModel }) {
    return {
      ...generateNewsletterTemplateData(data.model),
      'Language Count': data.newsletter?.languageVariations.length,
      'Preview Device': (
        {
          ...PlatformNames,
          desktop: 'Desktop',
          mobile: 'Mobile',
        } as any
      )[data.platform],
      'Push Preview Setting': data.previewType,
    };
  },

  errors(data: Data & ComposerEditableEntityAnalyticsData) {
    return generateErrorsData(data);
  },

  linkTracking(data) {
    return {
      Source: generateSource(data),
    };
  },

  addDataStarted(data: Data & AddDataStartedData) {
    return generateAddData(data);
  },

  addDataCompleted(data: Data & AddDataCompletedData) {
    return generateAddData(data);
  },

  retrySend(data: Data & RetryData) {
    return generateRetryData(data);
  },

  retrySendConfirmed(data: Data & RetryData & { overrideLimit: boolean }) {
    return {
      ...generateRetryData(data),
      'Override Message Limit': data.overrideLimit,
    };
  },

  messageMultiLanguageData(data: ComposerEditableEntityAnalyticsData) {
    return generateMessageMultiLanguageData(data);
  },

  minimapViewed(data: Data & MinimapViewedData) {
    return {
      ...generateCampaignActionData(data.action),
      'Time of Hover': data.hoverTimeInSeconds,
    };
  },

  minimapClicked(data: Data & MinimapClickedData) {
    return {
      'Campaign ID': data.clickedAction.campaign.id,
      'Campaign Action ID': data.clickedAction.id,
      'Workflow Item Type': actionType(data.clickedAction),
      'Message Type': actionType(data.currentAction),
    };
  },

  sendTest(data: Data & ComposerEditableEntityAnalyticsData) {
    let multiLanguageData = generateMessageMultiLanguageData(data);

    return {
      ...multiLanguageData,
      'Builder Type': getBuilderType(data),
    };
  },

  sendTestError(data: Data & ComposerEditableEntityAnalyticsData) {
    let baseData = generateBaseData(data);

    return {
      ...baseData,
      Language: data.language,
      'Language Count': data.languageCount,
      'Error Message': data.errorMessage,
    };
  },

  parcelData(data: ComposerEditableEntityAnalyticsData & { builderType: string }) {
    return {
      ...generateBaseData(data),
      'Builder Type': data.builderType,
    };
  },

  visualEditorToggledPanelData(
    data: ComposerEditableEntityAnalyticsData & {
      toggledOn: boolean;
      toggleMethod?: VisualEditorPropertiesPanelToggleMethod;
      visiblePanels: VisualEditorPanelName[];
      environmentId: string;
    },
  ) {
    return {
      ...generateMessageMultiLanguageData(data),
      workspace_id: data.environmentId,
      'Toggled On': data.toggledOn,
      'Visible Panels': data.visiblePanels,
      'Builder Type': getBuilderType(data),
      ...(data.toggleMethod != null ? { 'Toggle Method': data.toggleMethod } : {}),
    };
  },

  visualEditorEncompassComponentAddedData(
    data: ComposerEditableEntityAnalyticsData & {
      environmentId: string;
      componentName: string;
      presetLabel: string;
      using: string;
      isDuplicated: boolean;
    },
  ) {
    return {
      ...generateMessageMultiLanguageData(data),
      workspace_id: data.environmentId,
      'Component Tag': data.componentName,
      'Component Preset Label': data.presetLabel,
      Duplicated: data.isDuplicated,
      Method: data.using,
    };
  },

  visualEditorEncompassComponentConvertedData(
    data: ComposerEditableEntityAnalyticsData & {
      environmentId: string;
      originalComponentName: string;
      newComponentName: string;
      using: string;
    },
  ) {
    return {
      ...generateMessageMultiLanguageData(data),
      workspace_id: data.environmentId,
      'Original Component Tag': data.originalComponentName,
      'New Component Tag': data.newComponentName,
      Method: data.using,
    };
  },
};

export default Transforms;
